import { AnimatePresence, motion } from 'motion/react'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Button,
  Label,
  TextField,
  type TextFieldProps,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import SwapHorizIcon from '~icons/ic/baseline-swap-horiz'
import MdiTune from '~icons/mdi/tune'
import { openMembershipMoreCreditsModal } from 'src/components/Membership/MembershipMoreCreditsModal'
import { UIButton } from 'src/components/ui/Button'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import { UIInput } from 'src/components/ui/TextField'
import { UITooltipTriggerWrapper } from 'src/components/ui/Tooltip'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { useMe } from 'src/services/helpers/user'
import { useModelVersion } from 'src/stores/model'
import { cx } from 'src/utils/cx'
import { isMMDIT26AModel, isSDV1Model } from 'src/utils/model'
import { useTaskSubmitFormContext } from '../../../BaseTaskSubmitForm'
import {
  type AspectRatio,
  calculateSizeFromRatioAndTier,
  EXTRA_ASPECT_RATIOS_MMDIT26A,
  getOrientedAspectRatio,
  type Orientation,
  type SizeTier,
} from '../../../constants'
import { DEFAULT_XL_SIZE } from '../../constants'
import {
  ControlSectionMessage,
  ControlSectionMessageType,
} from '../ControlSection'
import {
  calculateSizeFromRatioAndRange,
  deriveRatiosForRange,
  findClosestRangeBySize,
  getSizeLimitsFromRanges,
  toPortraitKey,
  validateSizeFromRanges,
} from './apiAdapter'
import {
  findClosestAspectRatio,
  findClosestSizeTier,
  getModelSizeBounds,
  getModelSizeDivisor,
  validateSize,
} from './helper'
import { AspectRatioSelector, SizeTierSelector } from './selectors'
import { selectableButton } from './styles'
import type { SizeConfigResult } from './useSizeConfig'
export { selectableButton }

export function SizeInput(
  props: {
    value: number | undefined
    onChange: (value: number) => void
    onClamped?: () => void
    label: ReactNode
    divisor?: number
    min?: number
    max?: number
    showMembershipBorder?: boolean
    /** Exact preset edge values that bypass range clamping */
    presetValues?: ReadonlySet<number>
  } & Omit<TextFieldProps, 'onChange' | 'value'>,
) {
  const {
    value,
    onChange,
    onClamped,
    label,
    className,
    showMembershipBorder,
    divisor,
    min,
    max,
    presetValues,
    ...restProps
  } = props

  const [inputValue, setInputValue] = useState(String(value))

  useEffect(() => void setInputValue(String(value)), [value])

  function updateValue() {
    const numberedVal = Number(inputValue)

    let normalized = isNaN(numberedVal) ? (min ?? 512) : numberedVal
    const original = normalized

    // Skip clamping and step-alignment for exact preset values
    if (presetValues?.has(normalized)) {
      onChange(normalized)
      setInputValue(String(normalized))
      return
    }

    // Clamp to valid range before step-aligning
    if (min != null) normalized = Math.max(normalized, min)
    if (max != null) normalized = Math.min(normalized, max)

    if (divisor) {
      normalized = Math.floor(normalized / divisor) * divisor
      // Ensure step-aligned value stays within bounds
      if (max != null && normalized > max) {
        normalized = Math.floor(max / divisor) * divisor
      }
      if (min != null && normalized < min) {
        normalized = Math.ceil(min / divisor) * divisor
      }
    }

    if (normalized !== original) onClamped?.()
    onChange(normalized)
    setInputValue(String(normalized))
  }

  return (
    <TextField
      value={inputValue}
      type="text"
      className={className}
      {...restProps}
    >
      <Label>{label}</Label>
      <div
        className={cx(
          'w-full py-px px-px rounded-[11px]',
          showMembershipBorder && 'bg-gradient-purple-pink',
        )}
      >
        <UIInput
          variant="rounded-normal"
          inputMode="numeric"
          onChange={ev => setInputValue(ev.target.value)}
          onBlur={updateValue}
          value={inputValue}
          step={divisor} // divisor works as step
          className={cx(
            'w-full rounded-[10px]',
            showMembershipBorder && 'border-transparent',
          )}
        />
      </div>
    </TextField>
  )
}

export function ImageSizeSelectModal(props: {
  width?: number
  height?: number
  onConfirm?: (val: { width: number; height: number }) => void
  moreSelected?: boolean
  sizeConfig: SizeConfigResult
}) {
  const { onConfirm, sizeConfig } = props
  const { ranges, isApiDriven } = sizeConfig
  const { beingMember } = useMe()
  const { t } = useTranslation(['generation', 'action'])
  const {
    values: { modelId },
  } = useTaskSubmitFormContext()
  const modelVersion = useModelVersion(modelId)
  const modelAdditionalSizePresets = modelVersion?.extra?.presetSizes

  const modelType = modelVersion?.modelType ?? GenerationModelType.SdxlModel

  const defaultDivisors = getModelSizeDivisor({
    modelType,
    versionExtra: modelVersion?.extra,
  })

  const extraRatios = isMMDIT26AModel(modelType)
    ? EXTRA_ASPECT_RATIOS_MMDIT26A
    : undefined

  const initialTier = useMemo(() => {
    if (isApiDriven) {
      return findClosestRangeBySize(
        props.width ?? DEFAULT_XL_SIZE.width,
        props.height ?? DEFAULT_XL_SIZE.height,
        ranges,
      ).name
    }
    return findClosestSizeTier(
      props.width ?? DEFAULT_XL_SIZE.width,
      props.height ?? DEFAULT_XL_SIZE.height,
      modelType,
    )
  }, [props.width, props.height, modelType, isApiDriven, ranges])

  const initialRange = useMemo(
    () => (isApiDriven ? ranges.find(r => r.name === initialTier) : undefined),
    [isApiDriven, ranges, initialTier],
  )

  const initialRatio = useMemo(
    () =>
      findClosestAspectRatio(
        props.width ?? DEFAULT_XL_SIZE.width,
        props.height ?? DEFAULT_XL_SIZE.height,
        isApiDriven && initialRange
          ? deriveRatiosForRange(initialRange)
          : extraRatios?.filter(
              r =>
                !r.availableAt ||
                r.availableAt.includes(initialTier as SizeTier),
            ),
        isApiDriven,
      ),
    [
      props.width,
      props.height,
      extraRatios,
      initialTier,
      isApiDriven,
      initialRange,
    ],
  )
  const initialOrientation: Orientation = useMemo(() => {
    if (initialRatio.width === initialRatio.height) return 'portrait'
    return initialRatio.width < initialRatio.height ? 'portrait' : 'landscape'
  }, [initialRatio])

  const [selectedRatio, setSelectedRatio] = useState<AspectRatio>(initialRatio)
  const [orientation, setOrientation] =
    useState<Orientation>(initialOrientation)
  const [selectedTier, setSelectedTier] = useState<SizeTier | string>(
    initialTier,
  )
  const [width, setWidth] = useState(props.width ?? DEFAULT_XL_SIZE.width)
  const [height, setHeight] = useState(props.height ?? DEFAULT_XL_SIZE.height)

  const currentRange = useMemo(
    () =>
      isApiDriven
        ? (ranges.find(r => r.name === selectedTier) ?? ranges[0])
        : undefined,
    [isApiDriven, ranges, selectedTier],
  )

  const currentRangeRatios = useMemo(
    () =>
      isApiDriven && currentRange
        ? deriveRatiosForRange(currentRange)
        : undefined,
    [isApiDriven, currentRange],
  )

  const effectiveDivisor =
    isApiDriven && currentRange
      ? { width: currentRange.step, height: currentRange.step }
      : defaultDivisors

  /** Broadest valid bounds for manual size input (includes member ranges) */
  const sizeBounds = useMemo(() => {
    if (isApiDriven && ranges.length > 0) {
      return {
        minWidth: Math.min(...ranges.map(r => r.minWidth)),
        maxWidth: Math.max(...ranges.map(r => r.maxWidth)),
        minHeight: Math.min(...ranges.map(r => r.minHeight)),
        maxHeight: Math.max(...ranges.map(r => r.maxHeight)),
      }
    }
    const { min, max } = getModelSizeBounds(modelType)
    return { minWidth: min, maxWidth: max, minHeight: min, maxHeight: max }
  }, [isApiDriven, ranges, modelType])

  /** Exact preset edge values that bypass range clamping in custom input */
  const presetValues = useMemo(() => {
    if (!isApiDriven || ranges.length === 0) return undefined
    const edges = new Set<number>()
    for (const range of ranges) {
      for (const p of range.presets) {
        edges.add(p.width)
        edges.add(p.height)
      }
    }
    return edges
  }, [isApiDriven, ranges])

  const updateSizeFromSelection = useCallback(
    (ratio: AspectRatio, tier: SizeTier | string) => {
      if (isApiDriven) {
        const range = ranges.find(r => r.name === tier)
        if (range) {
          const newSize = calculateSizeFromRatioAndRange(ratio, range)
          setWidth(newSize.width)
          setHeight(newSize.height)
        }
      } else {
        const newSize = calculateSizeFromRatioAndTier(
          ratio,
          tier as SizeTier,
          modelType,
          defaultDivisors.width,
        )
        setWidth(newSize.width)
        setHeight(newSize.height)
      }
    },
    [isApiDriven, ranges, modelType, defaultDivisors.width],
  )

  const handleRatioChange = useCallback(
    (ratio: AspectRatio) => {
      setSelectedRatio(ratio)
      updateSizeFromSelection(ratio, selectedTier)
    },
    [selectedTier, updateSizeFromSelection],
  )

  const handleOrientationChange = useCallback(
    (newOrientation: Orientation) => {
      setOrientation(newOrientation)
      const newRatio = getOrientedAspectRatio(selectedRatio, newOrientation)
      setSelectedRatio(newRatio)
      updateSizeFromSelection(newRatio, selectedTier)
    },
    [selectedRatio, selectedTier, updateSizeFromSelection],
  )

  const handleTierChange = useCallback(
    (tier: SizeTier | string) => {
      setSelectedTier(tier)
      if (isApiDriven) {
        const newRange = ranges.find(r => r.name === tier)
        if (newRange) {
          const newRangeRatios = deriveRatiosForRange(newRange)
          const ratioKey = toPortraitKey(selectedRatio)
          const ratioExistsInRange = newRangeRatios.some(
            r => toPortraitKey(r) === ratioKey,
          )
          if (ratioExistsInRange) {
            updateSizeFromSelection(selectedRatio, tier)
          } else {
            const fallback = findClosestAspectRatio(
              selectedRatio.width,
              selectedRatio.height,
              newRangeRatios,
              true,
            )
            setSelectedRatio(fallback)
            updateSizeFromSelection(fallback, tier)
          }
        }
        return
      }
      const currentExtraRatio = extraRatios?.find(
        r =>
          (r.width === selectedRatio.width &&
            r.height === selectedRatio.height) ||
          (r.width === selectedRatio.height &&
            r.height === selectedRatio.width),
      )
      const isUnavailable =
        currentExtraRatio?.availableAt != null &&
        !currentExtraRatio.availableAt.includes(tier as SizeTier)
      if (isUnavailable) {
        const fallback = findClosestAspectRatio(
          selectedRatio.width,
          selectedRatio.height,
        )
        setSelectedRatio(fallback)
        updateSizeFromSelection(fallback, tier)
      } else {
        updateSizeFromSelection(selectedRatio, tier)
      }
    },
    [selectedRatio, updateSizeFromSelection, extraRatios, isApiDriven, ranges],
  )

  const sizeLimits = useMemo(
    () => (isApiDriven ? getSizeLimitsFromRanges(ranges) : null),
    [isApiDriven, ranges],
  )

  const { allowed: sizeIsAllowed, memberOnly: sizeIsMemberOnly } = isApiDriven
    ? validateSizeFromRanges(width, height, ranges)
    : validateSize({
        width,
        height,
        modelType,
        isMember: beingMember,
        additionalSizePresets: modelAdditionalSizePresets,
      })

  const [showClampHint, setShowClampHint] = useState(false)
  const clampTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const handleClamped = useCallback(() => {
    setShowClampHint(true)
    clearTimeout(clampTimerRef.current)
    clampTimerRef.current = setTimeout(() => setShowClampHint(false), 3000)
  }, [])
  useEffect(() => () => clearTimeout(clampTimerRef.current), [])

  const onConfirmClick = useCallback(
    (onClose: () => void) => {
      const proceed = () => {
        onConfirm?.({ width, height })
        onClose()
      }

      if (sizeIsMemberOnly && !beingMember) {
        openMembershipMoreCreditsModal({
          afterPurchaseSuccess: proceed,
          defaultMode: 'plans',
          trackingLocation: 'dimension-size',
        })
        onClose()
      } else {
        proceed()
      }
    },
    [beingMember, sizeIsMemberOnly, width, height, onConfirm],
  )

  const [openMoreModal, setOpenMoreModal] = useState(false)

  /**
   * Close without saving
   * So resetting state
   */
  const onCloseMoreModal = useCallback(() => {
    setWidth(props.width ?? DEFAULT_XL_SIZE.width)
    setHeight(props.height ?? DEFAULT_XL_SIZE.height)
    setSelectedRatio(initialRatio)
    setOrientation(initialOrientation)
    setSelectedTier(initialTier)
    setOpenMoreModal(false)
  }, [props.width, props.height, initialRatio, initialOrientation, initialTier])

  return (
    <>
      <UITooltipTriggerWrapper
        trigger={
          <Button
            aria-label={t('generation:size.more', 'More')}
            className={selectableButton({
              selected: props.moreSelected,
              className:
                'flex items-center justify-center py-1.5 px-1.5 rounded-lg flex-1',
            })}
            onPress={() => setOpenMoreModal(true)}
          >
            <MdiTune className="w-6 h-6 text-zinc-500 dark:text-zinc-300" />
          </Button>
        }
        placement="top"
      >
        <p className="px-2 py-1">{t('generation:size.more', 'More')}</p>
      </UITooltipTriggerWrapper>
      <UIDialog
        open={openMoreModal}
        onClose={onCloseMoreModal}
        dismissable
        className="mlg:w-96"
      >
        <UIDialogHeader heading={t('generation:size.dialog.title')}>
          {({ heading }) => (
            <>
              {heading}
              <p className="mt-2 text-neutral-500">
                {sizeLimits
                  ? t('generation:size.dialog.subtitle-dynamic', {
                      freeMin: sizeLimits.minSize,
                      freeMax: sizeLimits.freeMax,
                      memberMax: sizeLimits.memberMax,
                    })
                  : t('generation:size.dialog.subtitle')}
              </p>
            </>
          )}
        </UIDialogHeader>
        <UIDialogContent className="flex flex-col gap-4">
          <AspectRatioSelector
            selectedRatio={selectedRatio}
            orientation={orientation}
            onRatioChange={handleRatioChange}
            onOrientationChange={handleOrientationChange}
            apiRatios={currentRangeRatios}
            rangeRequiredMembershipTier={currentRange?.requiredMembershipTier}
            extraRatios={isApiDriven ? undefined : extraRatios}
            tier={isApiDriven ? undefined : (selectedTier as SizeTier)}
          />
          <SizeTierSelector
            selectedTier={selectedTier}
            selectedRatio={selectedRatio}
            onChange={handleTierChange}
            modelType={modelType}
            isMember={beingMember}
            divisor={effectiveDivisor.width}
            apiRanges={isApiDriven ? ranges : undefined}
          />
          <section>
            <Label className="block mb-2 text-sm font-medium">
              {t('generation:size.custom-size.label', 'Custom Size')}
            </Label>
            <AnimatePresence>
              {((!sizeIsAllowed && !sizeIsMemberOnly) || showClampHint) && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ControlSectionMessage
                    type={ControlSectionMessageType.Warning}
                    className="mb-2"
                  >
                    {sizeLimits
                      ? t('generation:size.custom-size.size-invalid-dynamic', {
                          min: sizeLimits.minSize,
                          max: sizeLimits.freeMax,
                          memberMax: sizeLimits.memberMax,
                        })
                      : isSDV1Model(modelType)
                        ? t('generation:size.custom-size.size-invalid-sdv1')
                        : t('generation:size.custom-size.size-invalid-sdxl')}
                  </ControlSectionMessage>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="flex items-center gap-2">
              <SizeInput
                value={width}
                onChange={value => setWidth(value)}
                onClamped={handleClamped}
                className="flex-1 min-w-0"
                label={t('generation:size.width')}
                showMembershipBorder={sizeIsMemberOnly}
                divisor={effectiveDivisor.width}
                min={sizeBounds.minWidth}
                max={sizeBounds.maxWidth}
                presetValues={presetValues}
                name="width"
              />
              <Button
                className="self-end mb-2 p-1 rounded-md text-primary/60 hover:text-primary hover:bg-primary/10 transition-colors"
                onPress={() => {
                  setWidth(height)
                  setHeight(width)
                }}
                aria-label="Swap width and height"
              >
                <SwapHorizIcon className="size-6" />
              </Button>
              <SizeInput
                value={height}
                onChange={value => setHeight(value)}
                onClamped={handleClamped}
                className="flex-1 min-w-0"
                label={t('generation:size.height')}
                showMembershipBorder={sizeIsMemberOnly}
                divisor={effectiveDivisor.height}
                min={sizeBounds.minHeight}
                max={sizeBounds.maxHeight}
                presetValues={presetValues}
                name="height"
              />
            </div>
          </section>
        </UIDialogContent>
        <UIDialogFooter>
          {({ close }) => (
            <UIButton
              variant="outlined"
              onPress={() => onConfirmClick(close)}
              className={cx(
                'w-full transition duration-500',
                sizeIsMemberOnly &&
                  'bg-gradient-purple-pink text-white border-white dark:border-modal-background hover:shadow-md active:shadow-md',
              )}
              disabled={!sizeIsAllowed && !sizeIsMemberOnly}
            >
              {t('action:confirm')}
            </UIButton>
          )}
        </UIDialogFooter>
      </UIDialog>
    </>
  )
}
